import { afterNextRender, Component, inject, isDevMode, LOCALE_ID } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { truthy } from '@nestech/utils';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmModalParams, ConfirmModalService } from '@web/common';
import {
  Authentication,
  EnvironmentService,
  InactivityService,
  MemberStore,
  PusherService,
  SavingsStore,
} from '@web/service';
import humanizeDuration from 'humanize-duration';
import { catchError, debounceTime, exhaustMap, filter, from, map, merge, of, take, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const defaultLogoutSeconds = 3 * 60;
const defaultNotificationSeconds = 2 * 60;

@Component({
  selector: 'cas-authenticated-root',
  template: `
    <router-outlet />
  `,
  imports: [RouterOutlet],
  providers: [InactivityService],
})
export class AuthenticatedRoot {
  #translateService = inject(TranslateService);
  #authService = inject(Authentication);
  #confirm = inject(ConfirmModalService);
  #inactivityService = inject(InactivityService);
  #localeId = inject(LOCALE_ID);

  #environment = inject(EnvironmentService).environment;

  #shouldLogout$ = (notificationSeconds: number, timeUntilLogout: number) => {
    const label = this.#translateService.instant('cas.inactivityBody', {
      notificationThresholdTime: humanizeDuration(notificationSeconds * 1000, { language: this.#localeId }),
      timeUntilLogout: humanizeDuration(timeUntilLogout, { language: this.#localeId }),
    });

    const confirmOptions: ConfirmModalParams = {
      notDismissable: true,
      header: 'cas.inactivityHeader',
      label,
      buttonConfig: { ok: { label: 'cas.keepLoggedIn' }, cancel: { label: 'cas.logout' } },
    };
    return from(this.#confirm.open(confirmOptions, { backdrop: 'static' })).pipe(
      map(() => false),
      catchError(() => of(true)),
    );
  };

  constructor() {
    const pusher = inject(PusherService);
    const store = inject(MemberStore);
    const isPension = inject(EnvironmentService).isPensionSystem();
    pusher
      .listen('LegalParty')
      .pipe(debounceTime(1000), takeUntilDestroyed())
      .subscribe(() => store.reload());
    if (isPension) {
      const savingsStore = inject(SavingsStore);
      pusher
        .listen('CoverCollection')
        .pipe(debounceTime(3000), takeUntilDestroyed())
        .subscribe(() => {
          savingsStore.savingsV2.reload();
          savingsStore.investmentProfileV2.reload();
        });
    }
    afterNextRender(() => this.#initInactivityLogout());
  }

  #initInactivityLogout(): void {
    const logoutSeconds = isDevMode()
      ? Number.MAX_SAFE_INTEGER
      : Math.max(this.#environment.logoutSeconds || defaultLogoutSeconds);
    const notificationSeconds = isDevMode()
      ? Number.MAX_SAFE_INTEGER
      : Math.max(this.#environment.notificationSeconds || defaultNotificationSeconds);

    this.#inactivityService
      .getInactivitySeconds()
      .pipe(
        filter(inactivty => inactivty > notificationSeconds),
        exhaustMap(inactivitySeconds => {
          const reaminingMs = (logoutSeconds - inactivitySeconds) * 1000;

          return merge(
            timer(reaminingMs).pipe(map(() => true)),
            this.#shouldLogout$(notificationSeconds, reaminingMs),
          ).pipe(take(1), truthy());
        }),
        take(1),
      )
      .subscribe({
        next: () => {
          this.#authService.logout();
        },
      });
  }
}
